import { GetPlayable } from "../constants";
import { DataManager } from "../dataManager";
import { OutOfCardsError } from "../deck";
import { BaseFlow } from "../engine/baseFlow";
import { Canceled } from "../engine/types";
import { MyLevel, SerializedGame } from "../level";
import { ResourceKind } from "../types";
import { GameOverFlow } from "./gameOverFlow";
import { PlaceBuildingFlow } from "./placeBuildingFlow";
import { ShopFlow } from "./shopFlow";

export type MainGameResult = Record<string, never>;

export class MainGameFlow extends BaseFlow<MainGameResult> {
  level: MyLevel;
  turnCount: number = 0;
  spawnsPerTurn: number = 1;
  turnsPerSpawn: number = 3;
  initialState!: SerializedGame;

  constructor(level: MyLevel) {
    super(level.engine.clock);
    this.level = level;
  }

  override async Execute() {
    console.log("Main game flow!");

    this.closeOnComplete(
      this.level.hudInterface.events.on("drawclicked", () => {
        if (this.level.cards.canDraw()) {
          this.level.cards.draw();
          this.level.turnBasedSystem.triggerTurn();
          BaseFlow.CancelCurrentFlow();
        }
      }),
      this.level.hudInterface.events.on("swapcardsinhand", (evt) => {
        this.level.cards.swapCardsInHand(evt.context.a, evt.context.b);
        BaseFlow.CancelCurrentFlow();
      }),
      this.level.hudInterface.events.on("movecardinhand", (evt) => {
        this.level.cards.moveCardInHand(evt.context.cardId, evt.context.newIndex);
        BaseFlow.CancelCurrentFlow();
      }),
      this.level.hudInterface.events.on("shopclicked", async () => {
        if (await this.handleShop()) BaseFlow.CancelCurrentFlow();
      }),
      this.level.hudInterface.events.on("helpclicked", () => {
        this.level.hudInterface.showHelp = true;
        BaseFlow.CancelCurrentFlow();
      }),
      this.level.hudInterface.events.on("helpclose", () => {
        this.level.hudInterface.showHelp = false;
        BaseFlow.CancelCurrentFlow();
      }),
    );
    this.initialState = this.level.serialize();
    this.clock.schedule(this.takeTurn.bind(this));
  }

  async takeTurn() {
    this.level.saveUndoSnapshot();
    try {
      const card = this.level.cards.activeCard;

      const currentFlow = new PlaceBuildingFlow(
        this.level,
        GetPlayable(card.name),
      );
      const tileResult = await MainGameFlow.runAndWait(currentFlow);

      if (tileResult == Canceled) {
        this.clock.schedule(this.takeTurn.bind(this));
        return;
      }

      if (tileResult.placed) {
        this.level.gameState.turnCount++;
        const playable = GetPlayable(tileResult.playableId);
        if (playable.providesUnlock?.length) {
          playable.providesUnlock.forEach((r) => {
            if (!this.level.gameState.seenResources.has(r)) {
              this.level.gameState.seenResources.add(r);
              this.firstSeenResource(r);
            }
          });
        }
        if (
          tileResult.combined &&
          !this.level.gameState.seenTransforms.has(playable.name)
        ) {
          this.level.gameState.seenTransforms.add(playable.name);
          console.log("First combined!", tileResult.playableId);
        }
        this.level.cards.play(card.cardId);
        if (this.level.cards.canDraw()) this.level.cards.draw();
        this.level.cards.debug();
        this.level.turnBasedSystem.triggerTurn();
      }
    } catch (e) {
      if (e instanceof OutOfCardsError) {
        await this.handleShop(true);
      } else throw e;
    }
    this.clock.schedule(this.takeTurn.bind(this));
  }

  firstSeenResource(r: ResourceKind) {
    console.log("First seen!", r);
  }

  private async handleShop(isOutOfCards: boolean = false) {
    console.log("handle shop");
    // Check if player can afford any pack
    const canAffordAnyPack = this.canAffordAnyPack();

    if (isOutOfCards && !canAffordAnyPack) {
      // Game over - no cards and no money for packs
      const gameOverFlow = new GameOverFlow(this.level);
      const gameOverResult = await MainGameFlow.runAndWait(gameOverFlow);

      if (gameOverResult !== Canceled && gameOverResult.restart) {
        // TODO: Implement restart logic
        this.level.restore(this.initialState);
      }
      return false;
    }

    this.level.saveUndoSnapshot();
    const shopFlow = new ShopFlow(this.level, isOutOfCards);
    const shopResult = await MainGameFlow.runAndWait(shopFlow);

    if (shopResult !== Canceled && shopResult.purchased) {
      console.log(
        `Purchased pack: ${shopResult.pack.name}, added ${shopResult.cardsAdded} cards`,
      );
    }
    return true;
  }

  private canAffordAnyPack(): boolean {
    const currentMoney = this.level.gameState.money;
    const seenResources = this.level.gameState.seenResources;

    const cardPacks = DataManager.getInstance().cardPacks;
    return cardPacks.some((pack) => {
      // Check money requirement
      if (currentMoney < pack.cost) return false;

      // Check resource requirements
      return pack.required.every((resource) => seenResources.has(resource));
    });
  }
}
